import { useEffect, useRef, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { connect } from "react-redux";
import { Form, Formik } from "formik";

import { ReactComponent as ShopifyLogo } from "../../../assets/images/shopifyLogo.svg";
import {
  buttonNameConstants,
  shopifyConstants,
  systemConstants,
  toastMessages,
  toastType,
} from "../../../constants";
import {
  authenticationActions,
  companyActions,
  shopifyActions,
  storeActions,
  systemActions,
} from "../../../redux/actions";
import {
  Button,
  LogoutButton,
  SelectDropDown,
  SelectStore,
  ShopifyCustomerSyncing,
  Topbar,
} from "../../../shared/components";
import RadioButton from "../../../shared/components/radioButton/RadioButton";
import {
  customToast,
  getShopifyConnectionLabel,
  isAdminLogin,
  isShopifyConnected,
  isSpinnerEnabled,
  isShopifyConnectionButtonDisabled,
  handleCreateSelectOptions,
  isReinstallShopifyApp,
} from "../../../shared/utility";
import { getItemFromStorage, removeItemFromStorage } from "../../../system";
import { shopifyService } from "../../../services";

const ShopifyConnection = (props) => {
  const {
    user,
    company,
    getStores,
    spinnerArray,
    currentStore,
    companyLogout,
    activateSpinner,
    deactivateSpinner,
    isActualUser = false,
    eligibleShopifyLocations = [],
    getEligibleShopifyLocations,
    assignExistingShopifyLocation,
    createShopifyLocation,
    getCurrentStoreCompanyDetails,
    getUpdatedCurrentStoreDetails,
  } = props;
  const [selectedStore, setSelectedStore] = useState(currentStore);
  const shopifyAppInstallStoreMiscDataId = getItemFromStorage(
    systemConstants.SHOPIFY_APP_INSTALL_STORE_MISC_DATA_ID
  );
  const isDummyCompany = company.id && company.isDummyCompany;
  const isConnected =
    isShopifyConnected(selectedStore) && !shopifyAppInstallStoreMiscDataId;
  const isConnecting = isSpinnerEnabled(
    spinnerArray,
    shopifyConstants.SET_SHOPIFY_CONNECTION
  );
  const isLoadingLocations = isSpinnerEnabled(
    spinnerArray,
    shopifyConstants.GET_ELIGIBLE_SHOPIFY_LOCATIONS
  );
  const eligibleLocationOptions = isLoadingLocations
    ? []
    : eligibleShopifyLocations.map((location) => ({
        label: location.name,
        value: location.id,
      }));
  const isStoreNeedsLocation = Boolean(
    company?.shopify &&
      !selectedStore?.shopify?.location &&
      !shopifyAppInstallStoreMiscDataId
  );
  const shouldShowShopifyLocations = isStoreNeedsLocation && !isDummyCompany;
  const isIndividualStoreNeedsLocation = isStoreNeedsLocation && isDummyCompany;
  const isLocationConfiguring =
    isConnecting || isLoadingLocations || isIndividualStoreNeedsLocation;
  const isAutoAssigningRef = useRef(false);

  const handleAssignLocation = (locationOption, locationId) => {
    if (locationOption === shopifyConstants.USE_EXISTING_SHOPIFY_LOCATION) {
      assignExistingShopifyLocation(
        selectedStore.id,
        locationId,
        selectedStore.companyId
      );
    }
    if (locationOption === shopifyConstants.CREATE_NEW_SHOPIFY_LOCATION) {
      createShopifyLocation(selectedStore.id, selectedStore.companyId);
    }
  };

  const connectStore = async (storeToConnect) => {
    if (!storeToConnect?.id || !shopifyAppInstallStoreMiscDataId) {
      customToast(toastMessages.SOMETHING_WENT_WRONG, toastType.ERROR);
      return;
    }

    try {
      activateSpinner(shopifyConstants.SET_SHOPIFY_CONNECTION);
      await shopifyService.connectShopifyAccessTokenFromStoreMiscDataToStore({
        storeId: storeToConnect.id,
        companyId: storeToConnect.companyId,
        shopifyAppInstallStoreMiscDataId,
      });
      getUpdatedCurrentStoreDetails(storeToConnect.id, (data) => {
        setSelectedStore(data);
      });
      if (storeToConnect?.companyId) {
        getCurrentStoreCompanyDetails(storeToConnect.companyId);
      }
      removeItemFromStorage(
        systemConstants.SHOPIFY_APP_INSTALL_STORE_MISC_DATA_ID
      );
    } catch (error) {
      const errorMessage =
        typeof error === "string"
          ? error
          : error?.message || toastMessages.SOMETHING_WENT_WRONG;
      customToast(errorMessage, toastType.ERROR);
    } finally {
      deactivateSpinner(shopifyConstants.SET_SHOPIFY_CONNECTION);
    }
  };

  const handleConnectShopify = async () => {
    if (isAdminLogin(user)) {
      customToast(toastMessages.SHOPIFY_ADMIN_CANNOT_INSTALL, toastType.ERROR);
      return;
    }
    if (!selectedStore && company?.id && !isDummyCompany) {
      customToast(
        toastMessages.SHOPIFY_SELECT_STORE_TO_CONNECT,
        toastType.ERROR
      );
      return;
    }
    if (
      !shopifyAppInstallStoreMiscDataId &&
      selectedStore.shopify &&
      selectedStore.shopify.isReinstallShopifyApp
    ) {
      try {
        activateSpinner(shopifyConstants.SET_SHOPIFY_CONNECTION);
        const res = await shopifyService.connectShopify({
          shop: selectedStore.shopify.shop,
          companyId: selectedStore.companyId || company.id,
          storeId: selectedStore.id,
        });
        if (res) {
          window.location.replace(res);
        } else {
          customToast(toastMessages.SOMETHING_WENT_WRONG, toastType.ERROR);
        }
      } catch (error) {
        const errorMessage =
          typeof error === "string"
            ? error
            : error?.message || toastMessages.SOMETHING_WENT_WRONG;
        customToast(errorMessage, toastType.ERROR);
      } finally {
        deactivateSpinner(shopifyConstants.SET_SHOPIFY_CONNECTION);
      }
      return;
    }

    if (!shopifyAppInstallStoreMiscDataId) {
      window.location.replace(process.env.REACT_APP_SHOPIFY_APP_INSTALL_URL);
      return;
    }

    connectStore(selectedStore);
  };

  useEffect(() => {
    if (!currentStore) {
      getStores(user);
    } else if (currentStore?.id) {
      getUpdatedCurrentStoreDetails(currentStore?.id);
    }
  }, []);

  useEffect(() => {
    if (currentStore?.id) {
      setSelectedStore(currentStore);
    }
    // if actual user and dummy company store means only one store then trigger the connect automatically as the user doesn't have to select the store as there is only one store to connect with shopify
    if (
      isActualUser &&
      currentStore?.id &&
      company?.id &&
      isDummyCompany &&
      currentStore.id &&
      shopifyAppInstallStoreMiscDataId
    ) {
      connectStore(currentStore);
    }
    if (!company?.id && currentStore?.companyId) {
      getCurrentStoreCompanyDetails(currentStore.companyId);
    }
  }, [currentStore, company]);

  useEffect(() => {
    const companyId = selectedStore?.companyId;
    if (isStoreNeedsLocation && companyId) {
      getEligibleShopifyLocations(companyId);
    }
  }, [
    selectedStore?.companyId,
    company?.id,
    isStoreNeedsLocation,
    getEligibleShopifyLocations,
  ]);

  useEffect(() => {
    if (!isIndividualStoreNeedsLocation) {
      isAutoAssigningRef.current = false;
      return;
    }

    if (
      eligibleShopifyLocations.length > 0 &&
      !isConnecting &&
      !isAutoAssigningRef.current &&
      selectedStore?.id
    ) {
      const primaryLocation = eligibleShopifyLocations[0];

      if (primaryLocation?.id) {
        isAutoAssigningRef.current = true;
        assignExistingShopifyLocation(
          selectedStore.id,
          primaryLocation.id,
          selectedStore.companyId
        );
      }
    }
  }, [
    isIndividualStoreNeedsLocation,
    eligibleShopifyLocations,
    isConnecting,
    selectedStore?.id,
    selectedStore?.companyId,
    assignExistingShopifyLocation,
  ]);

  return (
    <>
      {isActualUser ? (
        <div className="position-absolute top-0 end-0 mt-2 me-2">
          <LogoutButton
            onClick={companyLogout}
            className="logout-button position-static "
          />
        </div>
      ) : (
        <Topbar title="Shopify Connection"></Topbar>
      )}
      <Container>
        <Row>
          <Col
            md={isActualUser ? 7 : 5}
            className="mx-auto d-flex flex-column gap-3 billing-card-wrapper"
          >
            <ShopifyLogo className="mx-auto" width={128} />
            {
              // As shopify user will always be dummy company that's why making sure we only show this for actual users (actual users with company, actual users with dummy company don't need to select the store)
              isActualUser && !isDummyCompany ? (
                <>
                  <p className="w-100 m-0 p-0 text-center">
                    <b>{company.companyName}</b> <br />
                    {shouldShowShopifyLocations
                      ? "Select a Shopify Location to connect this Store."
                      : isConnecting
                      ? "Connecting to Shopify..."
                      : isShopifyConnectionButtonDisabled(
                          selectedStore,
                          isConnecting,
                          isActualUser
                        )
                      ? `You're all set! This store is connected to Shopify. You can close this window to continue, or select a different store.`
                      : `Select a store to connect.`}
                  </p>
                  <SelectStore
                    isDisabled={isConnecting}
                    value={handleCreateSelectOptions(
                      selectedStore,
                      selectedStore?.storeName
                    )}
                    onChange={(e) => {
                      setSelectedStore(e.value);
                    }}
                  />{" "}
                </>
              ) : isActualUser && !isConnected ? (
                <>
                  <p className="w-100 m-0 p-0 text-center">
                    Welcome, <b>{company.companyName}</b>
                    <br />
                    {isLocationConfiguring
                      ? "Connecting to Shopify..."
                      : "Please click the following button to install LootLedger."}
                  </p>
                </>
              ) : // if store is connected and a new app update is available then show this message for actual users
              isReinstallShopifyApp(currentStore, isActualUser, isConnected) ? (
                <>
                  <p className="w-100 m-0 p-0 text-center">
                    Welcome, <b>{selectedStore.storeName}</b>
                    <br />
                    Your store is connected to Shopify! An app update is now
                    available. Please click below to install the latest version.
                  </p>
                </>
              ) : // If the store is already connected then show the success message for actual users
              isActualUser && isConnected ? (
                <>
                  <p className="w-100 m-0 p-0 text-center">
                    Welcome, <b>{selectedStore.storeName}</b>
                    <br />
                    Your store has been successfully connected with Shopify! You
                    may close this window now.
                  </p>
                </>
              ) : (
                // Shopify user, don't show anything as we will render the text after button for shopify user
                <> </>
              )
            }
            {!shouldShowShopifyLocations && (
              <Button
                type="button"
                className={"w-100 mt-0"}
                handleClick={handleConnectShopify}
                isDisabled={isShopifyConnectionButtonDisabled(
                  selectedStore,
                  isLocationConfiguring,
                  isActualUser
                )}
                label={getShopifyConnectionLabel(
                  selectedStore,
                  isActualUser,
                  isLocationConfiguring
                )}
              />
            )}
            {shouldShowShopifyLocations && (
              <div className="d-flex flex-column gap-2">
                <Formik
                  key={selectedStore.id}
                  initialValues={{ locationOption: "", locationId: "" }}
                  onSubmit={({ locationOption, locationId }) =>
                    handleAssignLocation(locationOption, locationId)
                  }
                >
                  {({ values, setFieldValue }) => (
                    <Form className="d-flex flex-column gap-2">
                      <p className="m-0">Choose how to connect this Store.</p>
                      <RadioButton
                        radioButtons={[
                          shopifyConstants.CREATE_NEW_SHOPIFY_LOCATION,
                          shopifyConstants.USE_EXISTING_SHOPIFY_LOCATION,
                        ]}
                        name="locationOption"
                      />
                      {values.locationOption ===
                        shopifyConstants.CREATE_NEW_SHOPIFY_LOCATION && (
                        <p className="m-0">
                          A new Shopify Location named {selectedStore.storeName}{" "}
                          will be created using this Store's address and phone
                          number.
                        </p>
                      )}
                      {values.locationOption ===
                        shopifyConstants.USE_EXISTING_SHOPIFY_LOCATION && (
                        <SelectDropDown
                          menuPlacement="bottom"
                          placeHolder="Select Shopify Location"
                          options={eligibleLocationOptions}
                          value={eligibleLocationOptions.find(
                            (location) => location.value === values.locationId
                          )}
                          handleChange={(location) =>
                            setFieldValue("locationId", location?.value ?? "")
                          }
                          isClearable
                          isDisabled={isLoadingLocations || isConnecting}
                        />
                      )}
                      <Button
                        type="submit"
                        className="w-100"
                        isDisabled={
                          isLoadingLocations ||
                          isConnecting ||
                          !values.locationOption ||
                          (values.locationOption ===
                            shopifyConstants.USE_EXISTING_SHOPIFY_LOCATION &&
                            !values.locationId) ||
                          !selectedStore?.id
                        }
                        label={buttonNameConstants.SAVE}
                      />
                    </Form>
                  )}
                </Formik>
              </div>
            )}
            {!isActualUser && (
              <>
                <p className="w-100 ">
                  {isConnected && (
                    <>
                      The store is connected with
                      <b> {selectedStore?.shopify?.shop} </b>
                    </>
                  )}
                  All Customers will be synced automatically, and Inventory
                  Items will need to be synced selectively.
                </p>
                {isConnected && <ShopifyCustomerSyncing />}
              </>
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
};

//-------Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  currentStore: state.store.currentStore,
  spinnerArray: state.system.spinnerArray,
  company: state.company.currentStoreCompany,
  user: state.authentication.user,
  eligibleShopifyLocations: state.shopify.eligibleShopifyLocations,
});

//-------Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  activateSpinner: (data) => dispatch(systemActions.activateSpinner(data)),
  deactivateSpinner: (data) => dispatch(systemActions.deactivateSpinner(data)),
  getUpdatedCurrentStoreDetails: (storeId, callBack) =>
    dispatch(storeActions.getUpdatedCurrentStoreDetails(storeId, callBack)),
  getStores: (data) => dispatch(storeActions.getStores(data)),
  companyLogout: () => dispatch(authenticationActions.companyLogout()),
  getCurrentStoreCompanyDetails: (companyId) =>
    dispatch(companyActions.getCurrentStoreCompanyDetails(companyId)),
  getEligibleShopifyLocations: (companyId) =>
    dispatch(shopifyActions.getEligibleShopifyLocations(companyId)),
  assignExistingShopifyLocation: (storeId, locationId, companyId) =>
    dispatch(
      shopifyActions.assignExistingShopifyLocation(
        storeId,
        locationId,
        companyId
      )
    ),
  createShopifyLocation: (storeId, companyId) =>
    dispatch(shopifyActions.createShopifyLocation(storeId, companyId)),
});

//-------Export AddNewUser Component
export default connect(mapStateToProps, mapDispatchToProps)(ShopifyConnection);
